import { Edit, InfoOutline } from '@mui/icons-material';
import {
  Box,
  Breakpoint,
  IconButton,
  styled,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FC, PropsWithChildren, ReactNode } from 'react';
import { Column, Row } from '../Layout';

type FormFieldSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg';

const sizesMap: Record<FormFieldSize, string> = {
  xxs: '80px',
  xs: '120px',
  sm: '220px',
  md: '400px',
  lg: '500px',
};

const EditButton = styled(IconButton)({ padding: 0 });
const EditIcon = styled(Edit)({
  width: 16,
  height: 16,
});

const ColumnWrapper = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: FormFieldSize }>(({ size }) => ({
  width: '100%',
  maxWidth: size ? sizesMap[size] : '100%',
}));

const RowWrapper = styled(Row, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: FormFieldSize }>(({ size }) => ({
  width: '100%',
  maxWidth: size ? sizesMap[size] : '100%',
  alignItems: 'center',
}));

const LabelColumn = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'labelSize' && prop !== 'isHorizontal',
})<{ labelSize?: FormFieldSize; isHorizontal?: boolean }>(
  ({ labelSize, isHorizontal }) => ({
    flex: isHorizontal && labelSize ? `0 0 ${sizesMap[labelSize]}` : '0 0 auto',
  }),
);

const ChildrenWrapper = styled(Box)({
  width: '100%',
});

interface DotProps {
  color: string;
}
const Dot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'color',
})<DotProps>(({ theme, color }) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: color,
  marginLeft: theme.spacing(1),
}));

type Props = {
  label?: string | ReactNode;
  tooltip?: string;
  extraInformation?: string;
  size?: FormFieldSize;
  labelColorLight?: boolean;
  spacing?: keyof Theme['spacingSizes'];
  editable?: boolean;
  onEdit?: () => void;
  dotColor?: string;
  dotTooltipText?: string;
  layout?: 'horizontal' | 'vertical';
  labelSize?: FormFieldSize;
  breakpoint?: Breakpoint;
};

const FormField: FC<PropsWithChildren<Props>> = ({
  children,
  label,
  extraInformation,
  size,
  labelColorLight,
  tooltip,
  spacing = 'sm',
  editable = false,
  onEdit,
  dotColor,
  dotTooltipText,
  layout = 'vertical',
  labelSize,
  breakpoint,
}) => {
  const theme = useTheme();
  const isCollapsed =
    useMediaQuery(theme.breakpoints.down(breakpoint ?? 'xs')) && !!breakpoint;
  const isHorizontal = layout === 'horizontal' && !isCollapsed;
  const Wrapper = isHorizontal ? RowWrapper : ColumnWrapper;
  return (
    <Wrapper size={size} spacing={spacing} as={isHorizontal ? Row : Column}>
      {!!label &&
        (typeof label === 'string' ? (
          <LabelColumn
            spacing="xs"
            labelSize={labelSize}
            isHorizontal={isHorizontal}
          >
            <Row centered spacing="xxs">
              <Typography
                color={labelColorLight ? 'grey.600' : undefined}
                variant="h6"
              >
                {label}
              </Typography>
              {tooltip ? (
                <Tooltip title={tooltip}>
                  <InfoOutline fontSize="inherit" />
                </Tooltip>
              ) : null}
              {editable ? (
                <EditButton size="small" onClick={onEdit}>
                  <EditIcon />
                </EditButton>
              ) : null}
              {dotColor ? (
                <Tooltip title={dotTooltipText}>
                  <Dot color={dotColor} />
                </Tooltip>
              ) : null}
            </Row>
            {!!extraInformation && (
              <Typography variant="body2">{extraInformation}</Typography>
            )}
          </LabelColumn>
        ) : (
          label
        ))}

      <ChildrenWrapper>{children}</ChildrenWrapper>
    </Wrapper>
  );
};

export default FormField;
